<template>
  <!-- The server has a hard time rendering the popover, so we need to use a client-only component to avoid hydration errors, a fallback is provided though.  -->
  <ClientOnly>
    <RevPopover
      contentClassNames="z-10"
      :modifiers="[
        {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`
          },
        },
      ]"
      position="bottom-start"
    >
      <template #trigger="{ show, hide, isOpen, ...triggerProps }">
        <BackBoxRangeInputTrigger
          :id="inputId"
          :disabled="false"
          :focused="isOpen"
          :has-error="false"
          :has-selected-option="Boolean(displayValue)"
          :select-id="inputId"
          v-bind="triggerProps"
          @click="isOpen ? hide() : show()"
        >
          <template #label>
            {{ i18n(sharedTranslations.backboxPriceRangeType) }}
          </template>
          <template #value>
            <div v-if="Boolean(displayValue)" class="pl-12 pr-32 pt-20">
              {{ displayValue }}
            </div>
          </template>
        </BackBoxRangeInputTrigger>
      </template>
      <template #content="{ hide }">
        <RevCard
          class="bg-action-default-low shadow-long flex flex-col gap-24 px-12 py-16"
        >
          <form class="flex flex-col gap-24" @submit.prevent="hide">
            <p class="text-caption">
              {{ i18n(sharedTranslations.backboxPriceRangeTypeCaption) }}
            </p>
            <fieldset class="flex flex-col gap-8">
              <RevRadio
                id="backbox-price-range-type-percentage"
                v-model="backboxPriceRangeTypeValueForRadio"
                value="percentage"
              >
                <template #label>
                  {{ i18n(sharedTranslations.backboxPriceRangeTypePercentage) }}
                </template>
              </RevRadio>
              <RevRadio
                id="backbox-price-range-type-amount"
                v-model="backboxPriceRangeTypeValueForRadio"
                value="amount"
              >
                <template #label>
                  {{ i18n(sharedTranslations.backboxPriceRangeTypeAmount) }}
                </template>
              </RevRadio>
            </fieldset>

            <PercentageFields
              v-if="tempBackboxPriceRangeType === 'percentage'"
              v-model:backboxPriceMaxRangeValue="tempBackboxPriceMaxRange"
              v-model:backboxPriceMinRangeValue="tempBackboxPriceMinRange"
            />
            <AmountFields
              v-if="tempBackboxPriceRangeType === 'amount'"
              v-model:backboxPriceCurrencyCode="tempBackboxPriceRangeCurrency"
              v-model:backboxPriceMaxRangeValue="tempBackboxPriceMaxRange"
              v-model:backboxPriceMinRangeValue="tempBackboxPriceMinRange"
              :currency-options="props.currencyOptions"
            />
            <div class="flex flex-col gap-12">
              <RevButton
                class="grow"
                :disabled="!isFormValid"
                size="small"
                variant="primary"
                @click="handleApply(hide)"
              >
                {{ i18n(sharedTranslations.backboxPriceRangeTypeApply) }}
              </RevButton>
              <RevButton
                class="grow"
                size="small"
                variant="secondary"
                @click="handleReset"
              >
                {{ i18n(sharedTranslations.backboxPriceRangeTypeReset) }}
              </RevButton>
            </div>
          </form>
        </RevCard>
      </template>
    </RevPopover>
    <template #fallback>
      <BackBoxRangeInputTrigger
        :id="inputId"
        :disabled="false"
        :focused="false"
        :has-error="false"
        :has-selected-option="Boolean(displayValue)"
        :select-id="inputId"
      >
        <template #label>
          {{ i18n(sharedTranslations.backboxPriceRangeType) }}
        </template>
        <template #value>
          <div v-if="Boolean(displayValue)" class="pl-12 pr-32 pt-20">
            {{ displayValue }}
          </div>
        </template>
      </BackBoxRangeInputTrigger>
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { Currency } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevPopover } from '@ds/components/Popover'
import { RevRadio } from '@ds/components/Radio'

import sharedTranslations from '../../Filters.translations'
import type { InitialFilterValues } from '../../Filters.types'

import AmountFields from './components/AmountFields.vue'
import BackBoxRangeInputTrigger from './components/BackBoxRangeInputTrigger.vue'
import PercentageFields from './components/PercentageFields.vue'
import { validateBackBoxRangeInputValues } from './helpers'

const i18n = useI18n()

const props = defineProps<{
  id: string
  currencyOptions: { label: string; value: Currency }[]
}>()

const inputId = computed(() => `backbox-price-range-input-${props.id}`)

// Model values - these are the actual filter values
const backboxPriceRangeTypeValue = defineModel<
  InitialFilterValues['backboxPriceRangeType']
>('backboxPriceRangeType', {
  required: true,
})

const backboxPriceMinRangeValue = defineModel<
  InitialFilterValues['backboxPriceMinRange']
>('backboxPriceMinRange', {
  required: true,
})
const backboxPriceMaxRangeValue = defineModel<
  InitialFilterValues['backboxPriceMaxRange']
>('backboxPriceMaxRange', {
  required: true,
})
const backboxPriceRangeCurrencyValue = defineModel<
  InitialFilterValues['backboxPriceCurrencyCode']
>('backboxPriceCurrencyCode', {
  required: true,
})

const withOrWithoutBackbox = defineModel<InitialFilterValues['hasBackbox']>(
  'withOrWithoutBackbox',
  {
    required: true,
  },
)

// Temporary values for form inputs - these are used while editing
const tempBackboxPriceRangeType = ref<string>('')
const tempBackboxPriceMinRange = ref<string>('')
const tempBackboxPriceMaxRange = ref<string>('')
const tempBackboxPriceRangeCurrency = ref<string>('')

// RevRadio treats the field's default "" as a truthy value, (essentially setting both the radios as selected in ui)
// To circumvent this we use a custom getter/setter.
const backboxPriceRangeTypeValueForRadio = computed({
  get: () => tempBackboxPriceRangeType.value || false,
  set: (value: 'amount' | 'percentage' | '') => {
    tempBackboxPriceRangeType.value = value
  },
})

// Initialize temporary values from model values when they exist
watch(
  [
    backboxPriceRangeTypeValue,
    backboxPriceMinRangeValue,
    backboxPriceMaxRangeValue,
    backboxPriceRangeCurrencyValue,
  ],
  ([type, min, max, currency]) => {
    tempBackboxPriceRangeType.value = type || ''
    tempBackboxPriceMinRange.value = min || ''
    tempBackboxPriceMaxRange.value = max || ''
    tempBackboxPriceRangeCurrency.value = currency || ''
  },
  { immediate: true },
)

const isFormValid = computed(() => {
  return validateBackBoxRangeInputValues(
    tempBackboxPriceRangeType.value,
    tempBackboxPriceMinRange.value,
    tempBackboxPriceMaxRange.value,
    tempBackboxPriceRangeCurrency.value,
  )
})

const displayValue = computed(() => {
  // Check if model values are valid for display using the validation function
  if (
    !validateBackBoxRangeInputValues(
      backboxPriceRangeTypeValue.value,
      backboxPriceMinRangeValue.value,
      backboxPriceMaxRangeValue.value,
      backboxPriceRangeCurrencyValue.value,
    )
  ) {
    return undefined
  }

  if (backboxPriceRangeTypeValue.value === 'percentage') {
    return `${backboxPriceMinRangeValue.value} - ${backboxPriceMaxRangeValue.value}%`
  }

  if (backboxPriceRangeTypeValue.value === 'amount') {
    const minRange = i18n.price(backboxPriceMinRangeValue.value, {
      currency: backboxPriceRangeCurrencyValue.value as Currency,
    })
    const maxRange = i18n.price(backboxPriceMaxRangeValue.value, {
      currency: backboxPriceRangeCurrencyValue.value as Currency,
    })

    return `${minRange} - ${maxRange}`
  }

  return undefined
})

const handleReset = () => {
  // Clear temporary values
  tempBackboxPriceRangeType.value = ''
  tempBackboxPriceMinRange.value = ''
  tempBackboxPriceMaxRange.value = ''
  tempBackboxPriceRangeCurrency.value = ''

  // Clear model values
  backboxPriceRangeTypeValue.value = ''
  backboxPriceMinRangeValue.value = ''
  backboxPriceMaxRangeValue.value = ''
  backboxPriceRangeCurrencyValue.value = ''
}

const handleApply = (hide: () => void) => {
  // Copy temporary values to model values
  backboxPriceRangeTypeValue.value = tempBackboxPriceRangeType.value
  backboxPriceMinRangeValue.value = tempBackboxPriceMinRange.value
  backboxPriceMaxRangeValue.value = tempBackboxPriceMaxRange.value
  backboxPriceRangeCurrencyValue.value = tempBackboxPriceRangeCurrency.value

  // set to false to show listings without the backbox
  withOrWithoutBackbox.value = 'false'
  hide()
}
</script>
